import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, ArrowUpRight, X } from "lucide-react";
import { useLang, loc } from "../i18n";
import { Reveal, SectionHead } from "./Reveal";
import { scrollToId } from "../lib/scroll";

const CATS = ["fachadas", "pintura", "altura", "reparacao", "outro"];

const BeforeAfter = ({ before, after, labels }) => {
  const [pos, setPos] = useState(50);
  return (
    <div className="relative aspect-[4/3] w-full touch-none overflow-hidden border border-white/10" data-testid="before-after">
      <img src={after} alt="After" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 overflow-hidden" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <img src={before} alt="Before" className="absolute inset-0 h-full w-full object-cover" />
      </div>
      <div className="pointer-events-none absolute inset-y-0 w-0.5 bg-[#FF5A00]" style={{ left: `${pos}%` }}>
        <span className="absolute left-1/2 top-1/2 flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-[#FF5A00] text-[10px] font-bold text-white">
          ⇔
        </span>
      </div>
      <span className="pointer-events-none absolute left-3 top-3 bg-black/70 px-3 py-1 text-[10px] font-bold uppercase tracking-widest">{labels[0]}</span>
      <span className="pointer-events-none absolute right-3 top-3 bg-[#FF5A00] px-3 py-1 text-[10px] font-bold uppercase tracking-widest">{labels[1]}</span>
      <input
        data-testid="before-after-slider"
        type="range"
        min="0"
        max="100"
        value={pos}
        onChange={(e) => setPos(Number(e.target.value))}
        className="absolute inset-0 h-full w-full cursor-ew-resize opacity-0"
      />
    </div>
  );
};

export const Projects = ({ projects }) => {
  const { lang, t } = useLang();
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);
  const list = filter === "all" ? projects : projects.filter((p) => p.category === filter);

  return (
    <section id="projects" data-testid="projects-section" className="mx-auto max-w-[1500px] px-5 py-24 md:px-10 md:py-32">
      <SectionHead num={t.projects.num} title={t.projects.title} sub={t.projects.sub} testid="projects-head" />

      <Reveal className="mb-10 flex flex-wrap gap-2" data-testid="project-filters">
        {["all", ...CATS].map((c) => (
          <button
            key={c}
            data-testid={`filter-${c}`}
            onClick={() => setFilter(c)}
            className={`border px-5 py-2.5 text-xs font-bold uppercase tracking-widest transition-colors ${
              filter === c
                ? "border-[#FF5A00] bg-[#FF5A00] text-white"
                : "border-white/15 text-neutral-400 hover:border-white/40 hover:text-white"
            }`}
          >
            {c === "all" ? t.projects.all : t.projects.filters[c]}
          </button>
        ))}
      </Reveal>

      <motion.div layout className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        <AnimatePresence mode="popLayout">
          {list.map((p) => (
            <motion.article
              layout
              key={p.id}
              data-testid={`project-card-${p.id}`}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              onClick={() => setActive(p)}
              className="group relative aspect-[4/3] cursor-pointer overflow-hidden border border-white/10"
            >
              <img
                src={p.image}
                alt={loc(p.title, lang)}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-[linear-gradient(to_top,rgba(0,0,0,0.92)_0%,rgba(0,0,0,0.25)_55%,transparent_100%)]" />
              <div className="absolute inset-x-0 bottom-0 p-6">
                <div className="flex items-center gap-3 text-[11px] font-bold uppercase tracking-widest text-[#FF5A00]">
                  <span>{t.projects.filters[p.category] || p.category}</span>
                  <span className="h-px w-6 bg-[#FF5A00]/60" />
                  <span className="flex items-center gap-1 text-neutral-300">
                    <MapPin size={12} /> {p.city}
                  </span>
                </div>
                <h3 className="mt-2 font-display text-2xl md:text-3xl uppercase tracking-wide">{loc(p.title, lang)}</h3>
                <p className="mt-1 text-xs uppercase tracking-wider text-neutral-400">{loc(p.workType, lang)}</p>
                <p className="mt-3 max-h-0 overflow-hidden text-sm text-neutral-300 opacity-0 transition-[max-height,opacity] duration-500 group-hover:max-h-28 group-hover:opacity-100">
                  {loc(p.desc, lang)}
                </p>
                <span className="mt-3 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                  {t.projects.view}
                  <ArrowUpRight size={14} className="text-[#FF5A00]" />
                </span>
              </div>
            </motion.article>
          ))}
        </AnimatePresence>
      </motion.div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="project-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm"
          >
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[90vh] w-full max-w-3xl overflow-y-auto border border-white/10 bg-[#0A0A0A]"
            >
              <div className="flex items-start justify-between gap-6 p-6 md:p-8">
                <div>
                  <div className="flex items-center gap-3 text-[11px] font-bold uppercase tracking-widest text-[#FF5A00]">
                    <span>{t.projects.filters[active.category] || active.category}</span>
                    <span className="h-px w-6 bg-[#FF5A00]/60" />
                    <span className="flex items-center gap-1 text-neutral-300">
                      <MapPin size={12} /> {active.city}
                    </span>
                  </div>
                  <h3 className="mt-2 font-display text-3xl md:text-4xl uppercase tracking-wide">{loc(active.title, lang)}</h3>
                  <p className="mt-1 text-xs uppercase tracking-wider text-neutral-400">{loc(active.workType, lang)}</p>
                </div>
                <button
                  data-testid="project-modal-close"
                  onClick={() => setActive(null)}
                  className="border border-white/15 p-2 transition-colors hover:border-[#FF5A00] hover:text-[#FF5A00]"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="px-6 pb-8 md:px-8">
                {active.before && active.after ? (
                  <BeforeAfter before={active.before} after={active.after} labels={[t.projects.before, t.projects.after]} />
                ) : (
                  <img src={active.image} alt={loc(active.title, lang)} className="aspect-[4/3] w-full border border-white/10 object-cover" />
                )}
                <p className="mt-5 text-sm leading-relaxed text-neutral-300">{loc(active.desc, lang)}</p>
                <button
                  data-testid="project-modal-cta"
                  onClick={() => {
                    setActive(null);
                    setTimeout(() => scrollToId("#contacts"), 150);
                  }}
                  className="mt-7 flex items-center gap-3 bg-[#FF5A00] px-7 py-4 text-xs font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#E04F00]"
                >
                  {t.headerCta}
                  <ArrowUpRight size={15} />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
